import React, { createContext, useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  setUserContextHeaders,
  clearUserContextHeaders,
} from "../services/httpClient";

const baseUrl = import.meta.env.VITE_APP_BASE_URL;

export const UserContext = createContext();

const createInitialUserData = () => ({
  userId: "",
  name: "Student",
  fullName: "",
  email: "",
  handle: "",
  roleType: "SuperAdmin",
  role: "",
  is_verified: false,
  createdAt: "",
  school: "",
  city: "",
  board: "",
  grade: "",
  contactNumber: "",
  profilePhoto: null,
  pincode: "",
  goals: [],
  onboarding_checklist: {
    self_assessment: false,
    evaluated_answersheet: false,
  },
  learning: {},
  learning_current_level: {},
  token: null,
  mathLevel: "Proficient",
  completedChallenges: 7,
  currentStreak: 3,
  totalXP: 320,
  currentRank: "Math Explorer",
  // mathSkills: {
  //   linearEquations: { total: 12, mastered: 8 },
  //   quadraticEquations: { total: 9, mastered: 3 },
  //   functionsAndGraphs: { total: 15, mastered: 15 },
  // },
  // recentTests: [
  //   {
  //     id: 1,
  //     name: "Algebra Mid-Term Test",
  //     uploadedDays: 2,
  //     score: 42,
  //     total: 50,
  //     grade: "A-",
  //     strengths: ["Strong in factoring expressions"],
  //     improvements: ["Practice needed in solving word problems"],
  //   },
  //   {
  //     id: 2,
  //     name: "Coordinate Geometry Quiz",
  //     uploadedDays: 7,
  //     score: 18,
  //     total: 20,
  //     grade: "A+",
  //     strengths: [
  //       "Excellent at plotting points and lines",
  //       "Good understanding of distance formula",
  //     ],
  //     improvements: [],
  //   },
  // ],
});

export const UserProvider = ({ children }) => {
  const [userData, setUserData] = useState(() => createInitialUserData());

  const hasSyncedHeadersRef = useRef(false);

  const sanitizeContextValue = (value) => {
    if (value === null || value === undefined) {
      return null;
    }

    const normalized = String(value).trim();
    return normalized.length ? normalized : null;
  };

  // Helper function to extract numeric grade from strings like "10th Grade" -> "10"
  const extractNumericGrade = (grade) => {
    if (!grade) return undefined;

    // If it's already a number or numeric string, return it
    if (typeof grade === "number") {
      return String(grade);
    }

    const gradeStr = String(grade).trim();

    // Extract the first number from the string (e.g., "10th Grade" -> "10")
    const match = gradeStr.match(/^\d+/);
    if (match) {
      return match[0];
    }

    // If no number found, return the original value
    return gradeStr;
  };

  const mergeDefinedFields = (updates = {}) => {
    return Object.entries(updates).reduce((acc, [key, value]) => {
      if (value !== undefined) {
        acc[key] = value;
      }
      return acc;
    }, {});
  };

  const updateProfileDetails = (details = {}) => {
    setUserData((prev) => ({
      ...prev,
      ...mergeDefinedFields(details),
    }));
  };

  // Function to fetch user profile data
  const fetchUserProfile = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;

      const response = await axios.get(`${baseUrl}/v2/profile/user`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const profile = response.data || {};
      console.log("profile -- UserContext.jsx");
      console.log(profile);
      updateProfileDetails({
        userId: profile.userId || undefined,
        name: profile.fullName?.trim() || undefined,
        fullName: profile.fullName?.trim() || undefined,
        handle: profile.handle || undefined,
        email: profile.email || undefined,
        contactNumber: profile.contactNumber || undefined,
        school: profile.school || undefined,
        city: profile.city || undefined,
        board: profile.board || undefined,
        grade: extractNumericGrade(profile.grade),
        profilePhoto: profile.profilePhoto || undefined,
        pincode:
          profile.pincode !== undefined && profile.pincode !== null
            ? String(profile.pincode).trim()
            : "",
        roleType: profile.role || undefined,
        role: profile.role || undefined,
        is_verified: profile.is_verified ?? undefined,
        createdAt: profile.createdAt || undefined,
        goals: Array.isArray(profile.goals) ? profile.goals : undefined,
        token: profile.token ?? undefined,
        learning: profile.learning || undefined,
        onboarding_checklist: profile.onboarding_checklist || undefined,
        learning_current_level: profile.learning_current_level || undefined,
      });
    } catch (error) {
      console.error("Error fetching user profile:", error);
    }
  };

  // Function to update profile photo
  const updateProfilePhoto = (newPhotoUrl) => {
    updateProfileDetails({ profilePhoto: newPhotoUrl });
  };

  // Fetch profile data on mount
  useEffect(() => {
    fetchUserProfile();
  }, []);

  useEffect(() => {
    const boardValue = sanitizeContextValue(userData?.board);
    const gradeValue = sanitizeContextValue(userData?.grade);
    const hasValues = Boolean(boardValue || gradeValue);

    if (hasValues) {
      setUserContextHeaders({
        board: boardValue,
        grade: gradeValue,
      });
      hasSyncedHeadersRef.current = true;
      return;
    }

    if (hasSyncedHeadersRef.current) {
      clearUserContextHeaders();
      hasSyncedHeadersRef.current = false;
    }
  }, [userData?.board, userData?.grade]);

  const resetUserData = () => {
    setUserData(createInitialUserData());
    clearUserContextHeaders();
    hasSyncedHeadersRef.current = false;
  };

  return (
    <UserContext.Provider
      value={{
        userData,
        setUserData,
        fetchUserProfile,
        updateProfilePhoto,
        updateProfileDetails,
        resetUserData,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};
